import React, { useState, useEffect, useMemo } from "react";
import firebase from "../../firebase";
import { getVendorId } from "../../utils/rbac";
import "./vendorratings.css";

const db = () => firebase.firestore();

// ── Helpers ───────────────────────────────────────────────────────────────
const toDate = (ts) => {
  if (!ts) return null;
  if (ts.toDate) return ts.toDate();
  if (ts.seconds) return new Date(ts.seconds * 1000);
  return new Date(ts);
};

const fmtDate = (ts) => {
  const d = toDate(ts);
  if (!d) return "";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
};

// ── Star Row ──────────────────────────────────────────────────────────────
const Stars = ({ value, max = 5, size = 13 }) => (
  <span className="vr_stars">
    {Array.from({ length: max }, (_, i) => (
      <i
        key={i}
        className={i < Math.round(value) ? "fa fa-star" : "fa fa-star-o"}
        style={{ color: i < Math.round(value) ? "#ff9800" : "#e0e0e0", fontSize: size }}
      />
    ))}
  </span>
);

// ── Rating Distribution Bars ──────────────────────────────────────────────
const DistribBars = ({ ratings, field }) => {
  const total = ratings.length;
  return (
    <div className="vr_distrib_wrap">
      {[5, 4, 3, 2, 1].map((star) => {
        const count = ratings.filter((r) => Math.round(r[field] || 0) === star).length;
        const pct   = total ? (count / total) * 100 : 0;
        return (
          <div key={star} className="vr_distrib_row">
            <span className="vr_distrib_star">
              {star}&nbsp;<i className="fa fa-star" style={{ color: "#ff9800", fontSize: 10 }}></i>
            </span>
            <div className="vr_distrib_bar_wrap">
              <div className="vr_distrib_bar" style={{ width: `${pct}%` }}></div>
            </div>
            <span className="vr_distrib_count">{count}</span>
          </div>
        );
      })}
    </div>
  );
};

// ── Main Page ─────────────────────────────────────────────────────────────
const VendorRatings = () => {
  const vendorId = getVendorId();

  const [ratings,    setRatings]    = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [view,       setView]       = useState("salon");   // "salon" | "stylist"
  const [sortBy,     setSortBy]     = useState("newest"); // "newest"|"highest"|"lowest"
  const [filterStar, setFilterStar] = useState(0);        // 0 = all

  useEffect(() => {
    if (!vendorId) { setLoading(false); return; }
    setLoading(true);
    db().collection("rating")
      .where("vendorId", "==", vendorId)
      .get()
      .then((snap) => {
        const docs = snap.docs.map((d) => ({ _id: d.id, ...d.data() }));
        // Convert Firestore numeric fields to JS numbers (handles double/int)
        setRatings(docs.map((r) => ({
          ...r,
          salonRating:   Number(r.salonRating   ?? 0),
          stylistRating: Number(r.stylistRating ?? 0),
        })));
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, [vendorId]);

  // ── Aggregates ──────────────────────────────────────────────────────────
  const salonAvg   = useMemo(() =>
    ratings.length ? ratings.reduce((s, r) => s + r.salonRating,   0) / ratings.length : 0,
  [ratings]);

  const stylistAvg = useMemo(() =>
    ratings.length ? ratings.reduce((s, r) => s + r.stylistRating, 0) / ratings.length : 0,
  [ratings]);

  // Per-stylist breakdown
  const stylistBreakdown = useMemo(() => {
    const map = {};
    ratings.forEach((r) => {
      const sid = r.stylistId || "unknown";
      if (!map[sid]) map[sid] = { name: r.stylistName || "Unknown", total: 0, count: 0 };
      map[sid].total += r.stylistRating;
      map[sid].count++;
    });
    return Object.entries(map)
      .map(([sid, s]) => ({ sid, ...s, avg: s.total / s.count }))
      .sort((a, b) => b.avg - a.avg);
  }, [ratings]);

  // ── Filtered + sorted list ───────────────────────────────────────────────
  const activeField = view === "salon" ? "salonRating" : "stylistRating";

  const filtered = useMemo(() => {
    let arr = [...ratings];
    if (filterStar > 0) arr = arr.filter((r) => Math.round(r[activeField]) === filterStar);
    if (sortBy === "newest")  arr.sort((a, b) => (toDate(b.createdAt) || 0) - (toDate(a.createdAt) || 0));
    if (sortBy === "oldest")  arr.sort((a, b) => (toDate(a.createdAt) || 0) - (toDate(b.createdAt) || 0));
    if (sortBy === "highest") arr.sort((a, b) => b[activeField] - a[activeField]);
    if (sortBy === "lowest")  arr.sort((a, b) => a[activeField] - b[activeField]);
    return arr;
  }, [ratings, sortBy, filterStar, activeField]);

  // ── Render helpers ───────────────────────────────────────────────────────
  const ratingColor = (v) => v >= 4 ? "#43a047" : v >= 3 ? "#ff9800" : "#e53935";

  if (!vendorId) return (
    <div className="vr_page"><div className="vr_empty">Vendor ID not found. Please log in again.</div></div>
  );

  if (loading) return (
    <div className="vr_page"><div className="vr_loading"><i className="fa fa-spinner fa-pulse fa-2x"></i></div></div>
  );

  if (!ratings.length) return (
    <div className="vr_page">
      <div className="vr_card vr_empty">
        <i className="fa fa-star-o" style={{ fontSize: 40, color: "#e0e0e0" }}></i>
        <div style={{ marginTop: 12, fontSize: 15 }}>No ratings yet for this salon.</div>
        <div style={{ fontSize: 12, color: "#bdbdbd", marginTop: 4 }}>
          Customers can leave ratings after completing a booking.
        </div>
      </div>
    </div>
  );

  return (
    <div className="vr_page">

      {/* ── Page header ── */}
      <div className="vr_page_header">
        <div>
          <h2 className="vr_page_title"><i className="fa fa-star"></i> Ratings &amp; Reviews</h2>
          <div className="vr_page_sub">{ratings.length} total review{ratings.length !== 1 ? "s" : ""}</div>
        </div>
      </div>

      {/* ── Summary cards ── */}
      <div className="vr_summary_row">
        {/* Salon summary */}
        <div className={`vr_summary_card${view === "salon" ? " active" : ""}`}
          onClick={() => setView("salon")}>
          <div className="vr_summary_icon"><i className="fa fa-home"></i></div>
          <div className="vr_summary_label">Salon Rating</div>
          <div className="vr_summary_num" style={{ color: ratingColor(salonAvg) }}>
            {salonAvg.toFixed(1)}
          </div>
          <Stars value={salonAvg} />
          <div className="vr_summary_count">{ratings.length} review{ratings.length !== 1 ? "s" : ""}</div>
        </div>

        {/* Stylist summary */}
        <div className={`vr_summary_card${view === "stylist" ? " active" : ""}`}
          onClick={() => setView("stylist")}>
          <div className="vr_summary_icon"><i className="fa fa-cut"></i></div>
          <div className="vr_summary_label">Stylist Rating</div>
          <div className="vr_summary_num" style={{ color: ratingColor(stylistAvg) }}>
            {stylistAvg.toFixed(1)}
          </div>
          <Stars value={stylistAvg} />
          <div className="vr_summary_count">{ratings.length} review{ratings.length !== 1 ? "s" : ""}</div>
        </div>
      </div>

      {/* ── Distribution + per-stylist ── */}
      <div className="vr_detail_row">
        {/* Distribution */}
        <div className="vr_card vr_distrib_card">
          <div className="vr_card_header">
            <i className="fa fa-bar-chart"></i>
            {view === "salon" ? "Salon" : "Stylist"} Rating Distribution
          </div>
          <div className="vr_avg_row">
            <span className="vr_avg_big" style={{ color: ratingColor(view === "salon" ? salonAvg : stylistAvg) }}>
              {(view === "salon" ? salonAvg : stylistAvg).toFixed(1)}
            </span>
            <div>
              <Stars value={view === "salon" ? salonAvg : stylistAvg} size={16} />
              <div className="vr_avg_sub">{ratings.length} reviews</div>
            </div>
          </div>
          <DistribBars ratings={ratings} field={activeField} />
        </div>

        {/* Per-stylist breakdown */}
        {stylistBreakdown.length > 0 && (
          <div className="vr_card vr_stylist_break_card">
            <div className="vr_card_header">
              <i className="fa fa-cut"></i> Stylist Breakdown
            </div>
            {stylistBreakdown.map(({ sid, name, avg, count }) => (
              <div key={sid} className="vr_stylist_row">
                <div className="vr_stylist_avatar">
                  {(name || "?")[0].toUpperCase()}
                </div>
                <div className="vr_stylist_info">
                  <div className="vr_stylist_name">{name}</div>
                  <div className="vr_stylist_bar_wrap">
                    <div className="vr_stylist_bar" style={{ width: `${(avg / 5) * 100}%`, background: ratingColor(avg) }}></div>
                  </div>
                </div>
                <div className="vr_stylist_score">
                  <div style={{ fontSize: 16, fontWeight: 800, color: ratingColor(avg) }}>{avg.toFixed(1)}</div>
                  <div style={{ fontSize: 10, color: "#9e9e9e" }}>{count}x</div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* ── Review list controls ── */}
      <div className="vr_card">
        <div className="vr_controls_row">
          {/* View toggle */}
          <div className="vr_toggle_group">
            {[["salon", "fa-home", "Salon"], ["stylist", "fa-cut", "Stylist"]].map(([k, ic, l]) => (
              <button
                key={k}
                className={`vr_toggle_btn${view === k ? " active" : ""}`}
                onClick={() => { setView(k); setFilterStar(0); }}
              >
                <i className={`fa ${ic}`}></i> {l}
              </button>
            ))}
          </div>

          {/* Star filter */}
          <div className="vr_star_filter">
            {[0, 5, 4, 3, 2, 1].map((s) => (
              <button
                key={s}
                className={`vr_star_btn${filterStar === s ? " active" : ""}`}
                onClick={() => setFilterStar(s)}
              >
                {s === 0 ? "All" : <><i className="fa fa-star" style={{ color: filterStar === s ? "#fff" : "#ff9800", fontSize: 11 }}></i> {s}</>}
              </button>
            ))}
          </div>

          {/* Sort */}
          <select className="vr_sort_select" value={sortBy} onChange={(e) => setSortBy(e.target.value)}>
            <option value="newest">Newest first</option>
            <option value="oldest">Oldest first</option>
            <option value="highest">Highest rated</option>
            <option value="lowest">Lowest rated</option>
          </select>
        </div>

        {/* ── Review list ── */}
        <div className="vr_card_header" style={{ marginTop: 16 }}>
          <i className="fa fa-comments"></i>
          {view === "salon" ? "Salon" : "Stylist"} Reviews
          <span className="vr_count_badge">{filtered.length}</span>
        </div>

        {filtered.length === 0 ? (
          <div className="vr_empty" style={{ padding: "30px 0" }}>
            No {filterStar > 0 ? `${filterStar}-star ` : ""}{view} reviews found.
          </div>
        ) : (
          <div className="vr_review_list">
            {filtered.map((r, i) => {
              const rating = r[activeField];
              const review = view === "salon" ? r.salonReview : r.stylistReview;
              const isAnon = r.isAnonymous;

              return (
                <div
                  key={r._id || i}
                  className="vr_review_item"
                  style={i < filtered.length - 1 ? { borderBottom: "1px solid #f5f5f5" } : {}}
                >
                  {/* Left: avatar + info */}
                  <div className="vr_review_left">
                    <div
                      className="vr_review_avatar"
                      style={{
                        background: isAnon ? "#f5f5f5" : "rgba(255,152,0,.15)",
                        color:      isAnon ? "#bdbdbd" : "#ff9800",
                      }}
                    >
                      {isAnon
                        ? <i className="fa fa-user-secret"></i>
                        : (r.userName || "?")[0].toUpperCase()}
                    </div>

                    <div className="vr_review_body">
                      <div className="vr_review_top">
                        <span className="vr_review_name">
                          {isAnon ? "Anonymous" : r.userName || "—"}
                        </span>
                        {view === "stylist" && r.stylistName && (
                          <span className="vr_stylist_tag">
                            <i className="fa fa-cut"></i> {r.stylistName}
                          </span>
                        )}
                        {r.bookingId && (
                          <span className="vr_booking_tag">#{r.bookingId}</span>
                        )}
                      </div>

                      <div className="vr_review_meta">
                        <Stars value={rating} />
                        <span className="vr_review_date">{fmtDate(r.createdAt)}</span>
                        <span className="vr_both_ratings">
                          <i className="fa fa-home" style={{ color: "#9e9e9e", fontSize: 10 }}></i>
                          &nbsp;{r.salonRating.toFixed(1)}
                          &nbsp;&nbsp;
                          <i className="fa fa-cut" style={{ color: "#9e9e9e", fontSize: 10 }}></i>
                          &nbsp;{r.stylistRating.toFixed(1)}
                        </span>
                      </div>

                      {review
                        ? <div className="vr_review_text">"{review}"</div>
                        : <div className="vr_review_no_text">No written review</div>
                      }
                    </div>
                  </div>

                  {/* Right: score */}
                  <div className="vr_review_score" style={{ color: ratingColor(rating) }}>
                    {rating.toFixed(1)}
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default VendorRatings;
